import React, { useState, useEffect } from 'react';
import './AdminSignUp.css';
import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../../services/firebase";  // Make sure path is correct
import Swal from 'sweetalert2';
import SideImage from '../../SideImg/SideImg';
import CreateAcctBanner from '../../../assets/signupuserbanner.png';
import Navbar from '../../Navbar/Navbar';

const AdminSignUp = () => {
    const [formData, setFormData] = useState({
        yourName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const isValidEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    const evaluatePasswordStrength = (pwd) => {
        if (!pwd) return '';
        const strong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
        const medium = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;
        if (strong.test(pwd)) return 'Strong Password';
        if (medium.test(pwd)) return 'Medium Password';
        return 'Weak Password';
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'password') {
            setPasswordStrength(evaluatePasswordStrength(value));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.yourName) newErrors.yourName = 'Name is required';
        if (!formData.email || !isValidEmail(formData.email)) newErrors.email = 'Enter a valid email';
        if (!formData.password) newErrors.password = 'Password is required';
        if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleAdminSignUp = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        const normalizedEmail = formData.email.trim().toLowerCase();

        try {
            // 1. Create user in Firebase Authentication
            const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, formData.password);
            
            // 2. Send verification email
            await sendEmailVerification(userCredential.user);
            
            // 3. Save admin data to the `admins` collection in Firestore
            await setDoc(doc(db, 'admins', userCredential.user.uid), {
                name: formData.yourName,
                email: normalizedEmail,
                avatarUrl: '',
                role: "admin",
                createdAt: new Date().toISOString(),
                emailVerified: false
            });

            Swal.fire({
                title: 'Success!',
                text: 'Admin account created! Please check your email to verify your account.',
                icon: 'success'
            });
            // window.location.href = "/admin"; // Redirect to admin login or dashboard

        } catch (error) {
            console.error("Full error details:", error);
            let errorMessage = "Signup failed. Please try again.";
            
            switch(error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = "This email is already registered. Sign in instead.";
                    break;
                case 'auth/weak-password':
                    errorMessage = "Your password should be at least 6 characters.";
                    break;
                case 'auth/invalid-email':
                    errorMessage = "Please enter a valid email address.";
                    break;
                default:
                    errorMessage = error.message || "An unexpected error occurred.";
            }
            Swal.fire("Error", errorMessage, "error");
        } finally {
            setIsLoading(false);
        }
    };

  return (
    <>
        <div className='page__wrapper'>
            <Navbar />
            <div className='auth__container'>
                <SideImage sideBannerSrc={CreateAcctBanner} sideBannerAlt={"Alex Menswear Create an Account"}/>
                <div className="sign-up-container">
                    <div className="sign-up-box">
                        <div className="signup-header">
                            <h1>Create Your Admin Account</h1>                
                            <div className='sign-up-links'><p>Already have an Alex Menswear admin Account? <a href="/auth/amvoss/sign-in">Log In here.</a></p></div>
                        </div>
                        <form className='sign-up-form' onSubmit={handleAdminSignUp}> 
                            <div className="name-inputs">
                                <div className="form-group">
                                    <label htmlFor="yourName">Your Name</label>
                                    <input
                                        type="text"
                                        id="yourName"
                                        name='yourName'
                                        placeholder='Your Full Name'
                                        className='sign-up-input'
                                        value={formData.yourName}
                                        onChange={handleChange}
                                        required
                                    />
                                    {errors.yourName && <span className="error">{errors.yourName}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name='email'
                                    placeholder="Email Address"
                                    className='sign-up-input'
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                                {errors.email && <span className="error">{errors.email}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="password">Create Your Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    name='password'
                                    placeholder='Create Your New Password'
                                    className='sign-up-input'
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                                <span className={`password-strength ${passwordStrength.toLowerCase().replace(' ', '-')}`}>
                                    {passwordStrength}
                                </span>
                                {errors.password && <span className="error">{errors.password}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name='confirmPassword'
                                    placeholder='Confirm Your New Password'
                                    className='sign-up-input'
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                />
                                {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}
                            </div>
                            <button 
                                type="submit" 
                                className="sign-up-button" 
                                disabled={isLoading}
                            >
                                {isLoading ? 'Creating Admin Account...' : 'Create Account'}
                            </button>                           
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
}

export default AdminSignUp;
